import { Link } from "react-router-dom";
import { imgUrl, isBoosted } from "@/lib/api";
import ServiceCard from "@/components/ServiceCard";
import { RatingBadge } from "@/components/Stars";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Heart, MapPin, MessageSquare, Sparkles, Gavel, Truck, Package } from "lucide-react";

export default function ProductCard({ product, favorited, onToggleFav }) {
  if (product.listing_type === "service") return <ServiceCard product={product} favorited={favorited} onToggleFav={onToggleFav} />;
  const img = product.images && product.images.length ? imgUrl(product.images[0]) : null;
  const featured = isBoosted(product);
  return (
    <div
      data-testid={`product-card-${product.id}`}
      className={`group relative overflow-hidden rounded-xl border transition-all shadow-lg animate-fade-up ${featured ? "border-amber-500/60 bg-zinc-900/70 shadow-amber-950/30 ring-1 ring-amber-500/30" : "border-zinc-800 bg-zinc-900/60 hover:bg-zinc-900 hover:border-zinc-700"}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden bg-zinc-950">
          {img ? (
            <img
              src={img}
              alt={product.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 group-[.discreet]:blur-xl"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-zinc-700 font-serif text-lg">Geen foto</div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-zinc-950/90 to-transparent" />
          <div className="absolute top-3 left-3 flex flex-col gap-2 items-start">
            {featured && (
              <span data-testid={`featured-badge-${product.id}`} className="flex items-center gap-1 rounded-full bg-amber-500/90 backdrop-blur text-amber-950 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 shadow-lg shadow-amber-950/40">
                <Sparkles className="h-3 w-3" /> Uitgelicht
              </span>
            )}
            <VerifiedBadge show={product.seller_verified} compact testid={`verified-badge-${product.id}`} />
            <span className="rounded-full bg-rose-950/70 backdrop-blur text-rose-300 border border-rose-800/50 text-[10px] font-mono uppercase tracking-wider px-2.5 py-1">
              {product.condition}
            </span>
          </div>
          {onToggleFav && (
            <button
              data-testid={`favorite-button-${product.id}`}
              onClick={(e) => { e.preventDefault(); onToggleFav(product.id); }}
              className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-950/70 backdrop-blur border border-zinc-700 text-zinc-300 hover:text-rose-400 transition-colors"
            >
              <Heart className={`h-4 w-4 ${favorited ? "fill-rose-500 text-rose-500" : ""}`} />
            </button>
          )}
        </div>
      </Link>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/product/${product.id}`} className="font-serif text-lg text-zinc-100 leading-tight line-clamp-1 hover:text-rose-300 transition-colors">
            {product.title}
          </Link>
        </div>
        <div className="mt-1 flex items-center justify-between gap-2 text-xs text-zinc-500">
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {product.location || "Onbekend"}</span>
          <RatingBadge rating={product.seller_rating} testid={`product-rating-${product.id}`} />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="font-mono text-lg font-semibold text-rose-400">€ {Number(product.price).toFixed(2)}</span>
          {product.bid_count > 0 ? (
            <span data-testid={`bid-count-${product.id}`} className="flex items-center gap-1 rounded-full bg-rose-950/40 border border-rose-800/40 text-rose-300 text-[11px] font-medium px-2 py-0.5">
              <Gavel className="h-3 w-3" /> {product.bid_count} {product.bid_count === 1 ? "bod" : "biedingen"}
            </span>
          ) : (
            <span className="text-[11px] text-zinc-600 uppercase tracking-wide">{product.category}</span>
          )}
        </div>
        {product.highest_bid > 0 && (
          <div data-testid={`highest-bid-${product.id}`} className="mt-2 flex items-center gap-1.5 rounded-lg bg-emerald-950/30 border border-emerald-800/40 px-2.5 py-1.5 text-xs">
            <Gavel className="h-3 w-3 text-emerald-400" />
            <span className="text-emerald-300/80">Hoogste bod:</span>
            <span className="font-mono font-semibold text-emerald-300">€ {Number(product.highest_bid).toFixed(2)}</span>
          </div>
        )}
        {(product.pickup || product.shipping) && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
            {product.pickup && <span className="flex items-center gap-1"><Package className="h-3 w-3" /> Ophalen</span>}
            {product.shipping && (
              <span className="flex items-center gap-1"><Truck className="h-3 w-3" /> {Number(product.shipping_cost) > 0 ? `Verzenden € ${Number(product.shipping_cost).toFixed(2)}` : "Gratis verzenden"}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
